import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/synapse/Icon';
import { useDebounced } from '@/hooks/useDebounced';
import { useHasPermission } from '@/hooks/usePermissions';
import { useAptTickets } from '@/features/apt-counter/useAptTickets';
import { PatientList } from '@/features/patients/PatientList';
import { PatientSearchField } from '@/features/patients/PatientSearchField';
import { ageFromDob, sexLabelKey } from '@/features/patients/patientDisplay';
import { useResultNavigation } from '@/features/patients/useResultNavigation';
import { useRecordedVisits } from '@/features/visits/useVisits';
import type { Patient, PatientSex } from '@/types/patients';

/**
 * Where staff start typing up the doctor's paper. Three ways in, in the order
 * they are needed: a patient who took a number today and has not been recorded
 * yet; any other patient, by search (the paper sometimes arrives for someone
 * who never took a number); and a visit already recorded today, to correct it.
 */
export function VisitsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canSeeQueue = useHasPermission('apt_counter.read');
  const canSearchPatients = useHasPermission('patients.read');
  const ticketsQ = useAptTickets();
  const recordedQ = useRecordedVisits();

  const waiting = (ticketsQ.data?.tickets ?? []).filter((x) => x.status === 'waiting');
  const recorded = recordedQ.data?.visits ?? [];

  return (
    <div className="mx-auto flex w-full max-w-[920px] flex-col gap-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-[-0.02em]">{t('visits.title')}</h1>
        <p className="mt-1 text-[13.5px] text-muted">{t('visits.subtitle')}</p>
      </header>

      {canSeeQueue && (
        <Section title={t('visits.waitingTitle')} count={waiting.length}>
          {ticketsQ.isLoading ? (
            <Note>{t('common.loading')}</Note>
          ) : ticketsQ.isError ? (
            <Note error>{t('aptCounter.couldNotLoadQueue')}</Note>
          ) : waiting.length === 0 ? (
            <Note>{t('visits.waitingEmpty')}</Note>
          ) : (
            <ul className="divide-y divide-line">
              {waiting.map((ticket) => (
                <li key={ticket.id}>
                  <PersonRow
                    lead={String(ticket.number)}
                    name={ticket.patient.fullName}
                    sex={ticket.patient.sex}
                    dateOfBirth={ticket.patient.dateOfBirth}
                    actionLabel={t('visits.recordFor', {
                      number: ticket.number,
                      name: ticket.patient.fullName,
                    })}
                    onClick={() =>
                      navigate(`/visits/new/${ticket.patient.id}?ticket=${ticket.id}`)
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {canSearchPatients && (
        <Section title={t('visits.otherPatientTitle')}>
          <div className="p-4">
            <PatientPicker onPick={(p) => navigate(`/visits/new/${p.id}`)} />
          </div>
        </Section>
      )}

      <Section title={t('visits.recordedTitle')} count={recorded.length}>
        {recordedQ.isLoading ? (
          <Note>{t('common.loading')}</Note>
        ) : recordedQ.isError ? (
          <Note error>{t('visits.couldNotLoadRecorded')}</Note>
        ) : recorded.length === 0 ? (
          <Note>{t('visits.recordedEmpty')}</Note>
        ) : (
          <ul className="divide-y divide-line">
            {recorded.map((visit) => (
              <li key={visit.id}>
                <PersonRow
                  lead={new Date(visit.recordedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  name={visit.patient.fullName}
                  sex={visit.patient.sex}
                  dateOfBirth={visit.patient.dateOfBirth}
                  actionLabel={t('visits.editFor', { name: visit.patient.fullName })}
                  icon="edit"
                  onClick={() => navigate(`/visits/${visit.id}`)}
                />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <h2 className="flex-1 text-[14px] font-semibold tracking-[-0.01em]">{title}</h2>
        {count !== undefined && count > 0 && (
          <span className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-paper-3 px-1.5 text-[11.5px] font-semibold tabular-nums text-ink-2">
            {count}
          </span>
        )}
      </div>
      {children}
    </Card>
  );
}

function Note({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div
      role={error ? 'alert' : undefined}
      className={'px-4 py-6 text-[13px] ' + (error ? 'text-destructive' : 'text-muted')}
    >
      {children}
    </div>
  );
}

function PersonRow({
  lead,
  name,
  sex,
  dateOfBirth,
  actionLabel,
  icon = 'arrow_up_right',
  onClick,
}: {
  lead: string;
  name: string;
  sex: PatientSex;
  dateOfBirth: string | null;
  actionLabel: string;
  icon?: 'arrow_up_right' | 'edit';
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const age = ageFromDob(dateOfBirth);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={actionLabel}
      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <span className="w-14 shrink-0 font-mono text-[15px] font-bold tabular-nums">{lead}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold tracking-[-0.01em]">{name}</span>
        <span className="block text-[12px] text-muted">
          {[age != null ? `${age} ${t('patients.yrs')}` : null, t(sexLabelKey(sex))]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      <Icon name={icon} size={16} className="text-muted" />
    </button>
  );
}

/** Search-and-pick, for a patient who is not in today's queue. */
function PatientPicker({ onPick }: { onPick: (p: Patient) => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim(), 200);
  const searchRef = useRef<HTMLInputElement>(null);
  const { highlightedId, activeDescendantId, onKeyDown, setPatients } = useResultNavigation(onPick);
  return (
    // Input and list share one wrapper so ↑/↓/Enter typed in the input reach
    // the handler that drives the highlight — as on the registry.
    <div onKeyDown={onKeyDown} className="flex flex-col gap-3">
      <PatientSearchField
        inputRef={searchRef}
        value={query}
        onChange={setQuery}
        size="md"
        activeDescendantId={activeDescendantId}
      />
      {/* Nothing until something is typed: unprompted, the list would show
          recent patients, which here reads as a second queue. */}
      {debounced.length >= 2 && (
        <PatientList
          query={debounced}
          onSelect={onPick}
          // Registering a patient is the registry's job, with its own screen
          // and its own permission.
          onAddNew={() => navigate('/registry/new')}
          variant="row"
          highlightedId={highlightedId}
          onResultsChange={setPatients}
        />
      )}
    </div>
  );
}
